'use client';
import { FilterGroups } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/FilterGroups';
import type {
  FilterChipActivation,
  FilterToggle,
} from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/FilterGroups';
import { SortAndFilterPanel } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/SortAndFilterPanel';
import React, { useCallback } from 'react';
import type { VerticalType } from '../../types';
import {
  trackCitizenshipSelect,
  trackClearAll,
  trackFilterChipSelect,
  trackFilterToggle,
  trackIncomeEntered,
  trackPanelOpen,
  trackSortSelect,
} from './tracking';
import { usePanelTrackingContext } from './usePanelTrackingContext';

/**
 * Adds the analytics events, which are the only thing the app knows here. The
 * sort config comes from the page-level provider rather than being rebuilt: a
 * second copy can disagree with the one the ordering uses.
 */
export const SortAndFilterPanelWrapper: React.FC<{
  /**
   * Index of the first block the panel orders. The panel is a page field rather
   * than a block, so it has no index of its own, and the analytics plan wants
   * the position the panel appears at on the page.
   */
  blockIndex?: number;
  /** The page's vertical, which decides how a filter group is reported. */
  vertical?: VerticalType;
}> = ({ blockIndex, vertical }) => {
  const context = usePanelTrackingContext(blockIndex, vertical);

  const onSortChange = useCallback(
    (_value: string, label: string) => trackSortSelect(context, label),
    [context],
  );

  const onFilterChange = useCallback(
    (toggle: FilterToggle) => trackFilterToggle(context, toggle),
    [context],
  );

  const onChipActivate = useCallback(
    (chip: FilterChipActivation) => trackFilterChipSelect(context, chip),
    [context],
  );

  const onClearAll = useCallback(
    (source: 'header' | 'footer') => trackClearAll(context, source),
    [context],
  );

  const onCitizenshipChange = useCallback(
    (value: string) => trackCitizenshipSelect(context, value),
    [context],
  );

  const onIncomeCommit = useCallback(
    (value: number | undefined) => trackIncomeEntered(context, value),
    [context],
  );

  const onOpen = useCallback(() => trackPanelOpen(context), [context]);

  // No `sortVisible` guard here on purpose: the panel renders its Sort By
  // section only when it has options, and the layout hands it an empty list
  // when the sort is off. Returning null here instead left a filters-only page
  // with an empty left column while the blocks kept filtering by the URL.
  return (
    <SortAndFilterPanel
      triggerPlacement="floating"
      onSortChange={onSortChange}
      onClearAll={onClearAll}
      onCitizenshipChange={onCitizenshipChange}
      onIncomeCommit={onIncomeCommit}
      onOpen={onOpen}
    >
      <FilterGroups
        onFilterChange={onFilterChange}
        onChipActivate={onChipActivate}
      />
    </SortAndFilterPanel>
  );
};
